/**
 * Shared Finance FX facade for all Midpoint product surfaces.
 *
 * fxRatesApi remains the HTTP/cache transport. Product code should use this
 * facade so spotlight-pair policy and multi-series loading stay centralized.
 */
import {
  FINANCE_FX_BASE_CURRENCY,
  FINANCE_FX_DEFAULT_CURRENCY,
  FINANCE_FX_SPOTLIGHT_CURRENCIES,
  isFinanceFxSpotlightCurrency,
} from "../constants/financeFxConstants.js";
import { fetchFxRateHistory, fetchFxRates } from "./fxRatesApi.js";

function normalizeFinanceCurrency(value, fallback = FINANCE_FX_DEFAULT_CURRENCY) {
  const normalized = String(value || "").trim().toUpperCase();
  if (isFinanceFxSpotlightCurrency(normalized)) {
    return normalized;
  }
  return fallback;
}

/** Current COP-based snapshot shared by CurrencyProvider and Finance widgets. */
export function fetchFinanceFxSnapshot() {
  return fetchFxRates();
}

/** History for one spotlight pair, framed internally as 1 COP = X foreign. */
export function fetchFinanceFxHistory(currency, days, options = {}) {
  const normalized = normalizeFinanceCurrency(currency);
  return fetchFxRateHistory(normalized, days, {
    ...options,
    base: FINANCE_FX_BASE_CURRENCY,
  });
}

/**
 * Load multiple spotlight histories without failing the whole Finance surface
 * when one pair is unavailable.
 */
export async function fetchFinanceFxHistories(
  currencies = FINANCE_FX_SPOTLIGHT_CURRENCIES,
  days,
  options = {}
) {
  const normalizedCurrencies = [
    ...new Set(
      currencies
        .map((currency) => String(currency || "").trim().toUpperCase())
        .filter(isFinanceFxSpotlightCurrency)
    ),
  ];

  const settled = await Promise.allSettled(
    normalizedCurrencies.map((currency) =>
      fetchFinanceFxHistory(currency, days, options)
    )
  );

  const series = {};
  const errors = {};
  settled.forEach((result, index) => {
    const currency = normalizedCurrencies[index];
    if (result.status === "fulfilled") {
      series[currency] = result.value;
    } else {
      errors[currency] = result.reason;
    }
  });

  return {
    base: FINANCE_FX_BASE_CURRENCY,
    days,
    currencies: normalizedCurrencies,
    series,
    errors,
  };
}

export { normalizeFinanceCurrency };
